import { createStore } from 'zustand';

import { IModalProps, ModalContext } from '@notino/react-styleguide';
import {
  AddProductsToCartMutation,
  CartServiceInput,
} from '@notino/shared/definitions/types';

import { BottomSheetProps } from '@components/BottomSheet/BottomSheet';
import { ToggleDrawerFn } from '@components/BottomSheet/DrawerProvider';
import { RequestState } from '@containers/App/model';
import { isDesktop } from '@utils/helpers';

import { IAddToCartResult, IModal } from '../context/model';
import { ProductWithCount } from '../model';
import { runTrackingEventCartUpdate } from '../utils';

type HandleProductAddedToCartParams = {
  products: ProductWithCount[];
  result: IAddToCartResult;
  modal: IModal | null;
  response: AddProductsToCartMutation['AddProductsToCart'];
  services: CartServiceInput[];
};

type AddToCartStore = {
  sendState: RequestState;
  submitAttempts: number;
  mouseOverBuyButton: boolean;
  actions: {
    setMouseOverBuyButton: () => void;
    resetSendState: () => void;
    handleProductAddedToCart: (params: HandleProductAddedToCartParams) => void;
    handleIncreaseSubmitAttempts: () => void;
    handleFailedAddToCart: () => void;
  };
};

type CreateAddToCartStoreParams = {
  toggleModal: ReturnType<typeof ModalContext.useModalContext>['toggleModal'];
  toggleDrawer: ToggleDrawerFn;
  getModalAfterAddToCart?: (
    props: IModal &
      IAddToCartResult & {
        products: ProductWithCount[];
        response: AddProductsToCartMutation['AddProductsToCart'];
        services: CartServiceInput[];
      }
  ) => [JSX.Element, IModalProps, Omit<BottomSheetProps, 'children' | 'open'>?];
};

export const createAddToCartStore = ({
  toggleModal,
  getModalAfterAddToCart,
  toggleDrawer,
}: CreateAddToCartStoreParams) =>
  createStore<AddToCartStore>((set, get) => ({
    sendState: RequestState.DEFAULT,
    submitAttempts: 0,
    mouseOverBuyButton: false,
    actions: {
      setMouseOverBuyButton: () => set({ mouseOverBuyButton: true }),
      handleFailedAddToCart: () => set({ sendState: RequestState.FAILED }),
      resetSendState: () =>
        set({ sendState: RequestState.DEFAULT, submitAttempts: 0 }),

      handleIncreaseSubmitAttempts: () =>
        set((state) => ({
          sendState:
            state.submitAttempts === 0
              ? RequestState.IN_PROGRESS
              : RequestState.RETRYING,
          submitAttempts: state.submitAttempts + 1,
        })),

      handleProductAddedToCart: ({
        result,
        modal,
        products,
        response,
        services,
      }) => {
        if (!result?.success && !result?.notification) {
          get().actions.handleFailedAddToCart();
          return;
        }

        set({ submitAttempts: 0, sendState: RequestState.DONE });

        products.forEach(({ id, count }) =>
          runTrackingEventCartUpdate({
            productId: id,
            quantity: count,
            action: 'add',
          })
        );

        if (!modal) {
          return;
        }

        const modalProps = getModalAfterAddToCart?.({
          products,
          response,
          services,
          ...modal,
          ...result,
        });

        if (modalProps) {
          const [content, modalOptions, bottomSheetOptions] = modalProps;

          if (isDesktop()) {
            toggleModal(content, modalOptions);
          } else {
            toggleDrawer(content, bottomSheetOptions);
          }
        }
      },
    },
  }));
